import type { Organisation } from '@esf/shared'

interface OrgStats {
  organisationId: string
  assetCount: number
  userCount: number
  workOrderCount: number
  iotDeviceCount: number
}

interface OrganisationDetailModalProps {
  organisation: Organisation
  stats?: OrgStats
  onClose: () => void
  onEdit: () => void
  onToggleEnabled: () => void
  onViewDashboard: () => void
}

const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

export default function OrganisationDetailModal({
  organisation,
  stats,
  onClose,
  onEdit,
  onToggleEnabled,
  onViewDashboard,
}: OrganisationDetailModalProps) {
  return (
    <div className="space-y-6">
      {/* Header with Logo/Initial */}
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-xl bg-esf-navy/10 flex items-center justify-center text-esf-navy text-2xl font-bold">
          {organisation.name.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900">{organisation.name}</h2>
            {organisation.isEsfOrg && (
              <span className="badge bg-esf-coral/10 text-esf-coral text-xs">Platform Organisation</span>
            )}
          </div>
          <p className="text-sm text-gray-500 font-mono mt-1">{organisation.slug}</p>
          <div className="mt-2">
            <span className={`badge ${organisation.enabled !== false ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'}`}>
              {organisation.enabled !== false ? 'Active' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-700">{stats?.assetCount ?? 0}</div>
          <div className="text-xs text-blue-600">Products</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-purple-700">{stats?.userCount ?? 0}</div>
          <div className="text-xs text-purple-600">Users</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-700">{stats?.workOrderCount ?? 0}</div>
          <div className="text-xs text-amber-600">Work Orders</div>
        </div>
        <div className="bg-teal-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-teal-700">{stats?.iotDeviceCount ?? 0}</div>
          <div className="text-xs text-teal-600">IoT Devices</div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h3>
        <div className="space-y-2">
          {organisation.contactEmail ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400">
                {renderIcon('M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', 'w-4 h-4')}
              </span>
              <a href={`mailto:${organisation.contactEmail}`} className="text-esf-navy hover:underline">
                {organisation.contactEmail}
              </a>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No email provided</p>
          )}
          {organisation.contactPhone && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400">
                {renderIcon('M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', 'w-4 h-4')}
              </span>
              <span className="text-gray-700">{organisation.contactPhone}</span>
            </div>
          )}
          {organisation.address && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-gray-400 mt-0.5">
                {renderIcon('M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z', 'w-4 h-4')}
              </span>
              <span className="text-gray-700">{organisation.address}</span>
            </div>
          )}
        </div>
      </div>

      {/* Timestamps */}
      <div className="text-xs text-gray-500 space-y-1">
        {organisation.createdAt && (
          <p>Created: {new Date(organisation.createdAt).toLocaleString()}</p>
        )}
        {organisation.updatedAt && (
          <p>Last updated: {new Date(organisation.updatedAt).toLocaleString()}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
        <button
          className="btn btn-primary"
          onClick={onViewDashboard}
        >
          {renderIcon('M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z')}
          View Dashboard
        </button>
        <button
          className="btn btn-outline"
          onClick={onEdit}
        >
          {renderIcon('M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z')}
          Edit
        </button>
        {!organisation.isEsfOrg && (
          <button
            className={`btn ${organisation.enabled !== false ? 'btn-secondary' : 'bg-success-50 text-success-600 hover:bg-success-100'}`}
            onClick={onToggleEnabled}
          >
            {organisation.enabled !== false ? 'Disable' : 'Enable'}
          </button>
        )}
        <button
          className="btn btn-secondary ml-auto"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  )
}
